<script setup>
import { storeToRefs } from 'pinia';
import { reactive, computed, onMounted, onBeforeUnmount, nextTick, toRaw, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useAddressFilterStore } from '@stores/useAddressFilterStore';
import { eventBus } from '@tools/event-bus';
import { useAdafStore } from '@stores/useAdafStore';
import { trans } from '@elements/translation';
import SpinnerContainer from '@components/SpinnerContainer.vue';
import AddressFilter from '@molecules/components/AddressFilter.vue';
import SliderInContainer from '@pages/pay/general/SliderInContainer.vue';
import AdafFiltersModal from './AdafFiltersModal.vue';
import AdafDownloadCustomDatesModal from './AdafDownloadCustomDatesModal.vue';
import AdafStats from './AdafStats.vue';
import AdafTable from './AdafTable.vue';
import { adafMixins } from './mixins';

const adafStore = useAdafStore();
const { selectedAddressesIds, selectedAddressesIdsString } = storeToRefs(useAddressFilterStore());

const { fetchPayments, setData, setMonthBefore } = adafStore;
const {
  addressesSelectedOnFilter,
  displayDate,
  lines,
  loading,
  differenceTenant,
  salesRevenues,
  differenceSalesRevenues,
  sumOneShotFee,
  sumRecurringFee,
} = storeToRefs(adafStore);

const router = useRouter();

const state = reactive({
  initialUserSettingsValue: false,
  accountManager: {},
  showFiltersModal: false,
  userSetting: {},
  deadlineDay: 1,
  showDownloadOptions: false,
  showDownloadCustomDatesModal: false,
});

const actualMonthFormatted = () => {
  const date = new Date();
  // It's normal ! That's the expected format by the backend
  const formatter = new Intl.DateTimeFormat('fr-CA', { year: 'numeric', month: '2-digit' });
  return formatter.format(date).replace('-', '');
};

const triggerMonthDisplay = async (month = null) => {
  const route = {
    name: 'adaf',
    query: { addresses: selectedAddressesIdsString.value },
  };
  route.params = { month: month ?? actualMonthFormatted() };
  router.push(route);
  await fetchPayments({ month: month ?? actualMonthFormatted(), addressIds: selectedAddressesIds.value, addressIdsString: selectedAddressesIdsString.value });
};

const isDeadlinePassed = computed(() => {
  const currentDay = (new Date()).getDate();

  return !(currentDay >= 1 && currentDay <= state.deadlineDay);
});

const countActiveFilters = computed(() => (selectedAddressesIds.value.length ? 1 : 0));

const validationDate = computed(() => {
  const date = new Date(displayDate.value);
  date.setDate(state.deadlineDay);
  return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
});

const validatedMonth = computed(() => {
  const date = new Date(displayDate.value);
  date.setDate(state.deadlineDay);
  return new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' }).format(date);
});

const toggleOptions = async () => {
  eventBus.dispatchEvent(new Event('offer-close-modale'));
  state.showDownloadOptions = !state.showDownloadOptions;
  await nextTick();
};

const formatMonthForRoute = (date) =>
  /**
   * getMonth starts at 0 - we need to display date with the following format YYYYMM
   * @see https://developer.mozilla.org/fr/docs/Web/JavaScript/Reference/Objets_globaux/Date/getMonth
   */
  // eslint-disable-next-line implicit-arrow-linebreak
  `${date.getFullYear()}${date.toLocaleDateString('fr', { month: '2-digit' })}`;
const updateMonth = (delta) => {
  displayDate.value = new Date(displayDate.value.setMonth(displayDate.value.getMonth() + delta));
  triggerMonthDisplay(formatMonthForRoute(displayDate.value));
};

const nextPage = () => updateMonth(1);
const prevPage = () => updateMonth(-1);

const closeCustomDatesModal = () => {
  state.showDownloadCustomDatesModal = false;
  state.showDownloadOptions = false;
};

const closeFilters = () => {
  state.showFiltersModal = false;
};

const openFilters = () => {
  state.showFiltersModal = true;
};

const openCustomDatesModal = () => {
  state.showDownloadCustomDatesModal = true;
};

const onSelectDates = (dates) => {
  adafMixins.methods.downloadWithCustomDates(dates.startMonthToSubmit, dates.endMonthToSubmit);
};

const onModalFilter = ({ addresses }) => {
  addressesSelectedOnFilter.value = toRaw(addresses).map((address) => toRaw(address).id);
  triggerMonthDisplay();
};
const handleDealPaymentPriceUpdated = () => {
  fetchPayments({ month: formatMonthForRoute(new Date()) });
};
const handleOfferCloseModale = () => {
  state.showDownloadOptions = false;
};

watch(selectedAddressesIds, () => {
  triggerMonthDisplay();
});

onMounted(() => {
  setData(window.initialADAFState);
  setMonthBefore(window.initialADAFState.previous_month);

  state.deadlineDay = window.initialADAFState.settings.adaf_deadline;

  eventBus.addEventListener('dealpayment-price-updated', handleDealPaymentPriceUpdated);

  eventBus.addEventListener('offer-close-modale', handleOfferCloseModale);

  // @Todo do something pls, see https://bureaux-a-partager.atlassian.net/browse/MATCH-9839
  window.addEventListener('click', handleOfferCloseModale);
});

onBeforeUnmount(() => {
  eventBus.removeEventListener('dealpayment-price-updated', handleDealPaymentPriceUpdated);
  eventBus.removeEventListener('offer-close-modale', handleOfferCloseModale);
  window.removeEventListener('click', handleOfferCloseModale);
});
</script>

<template>
  <div :key="$route.fullPath" class="adaf" data-cy="adaf">
    <SliderInContainer />
    <SpinnerContainer v-if="loading" />

    <h1 class="adaf__title">
      {{ trans('organization.adaf.title') }}
    </h1>

    <div v-if="adafMixins.methods.isThisCurrentMonth(displayDate)" class="adaf-b__side adaf-b-side top">
      <!-- eslint-disable vue/no-v-html -->
      <p
        v-if="!isDeadlinePassed"
        class="notice yellow"
        v-html="trans('organization.adaf.details.update_before_date', { date: validationDate })"
      />

      <p
        v-if="isDeadlinePassed"
        class="notice green"
        v-html="trans('organization.adaf.details.updated', { date: validatedMonth })"
      />
      <!-- eslint-enable vue/no-v-html -->
    </div>

    <div class="adaf-b__side adaf-b-side top overflow-stats">
      <div class="adaf-b-side__stats">
        <AdafStats
          :value="lines.length"
          :title="trans('organization.adaf.details.tenant')"
          icon="users"
          :percent="differenceTenant"
        />
        <div class="overflow-stats__separator" />
        <AdafStats
          :value="salesRevenues"
          :title="trans('organization.adaf.details.ca')"
          icon="money"
          euros
          :percent="differenceSalesRevenues"
        />
        <div class="overflow-stats__separator" />
        <AdafStats
          :value="sumRecurringFee + sumOneShotFee"
          :title="trans('organization.adaf.details.commission', { company_name: $companyName })"
          icon="percent"
          euros
        />
      </div>
    </div>

    <div class="adaf__body adaf-b">
      <div class="adaf-b__side adaf-b-side top">
        <div class="adaf-b-side__months">
          <span class="previous" @click="prevPage" />
          <span>{{ adafMixins.methods.displayedMonthLabel(displayDate) }}</span>
          <span
            class="next"
            :class="{ 'next__hidden': adafMixins.methods.isThisCurrentMonth(displayDate) }"
            @click="nextPage"
          />
        </div>
      </div>

      <div class="adaf-b__main adaf-b-main top">
        <div class="adaf-b-main__controls adaf-b-main-controls">
          <div class="adaf-b-main-controls__left adaf-b-main-controls-left">
            <AddressFilter class="adaf-b-main-controls-left__address-filter" />
          </div>
          <div class="adaf-b-main-controls__right adaf-b-main-controls-right">
            <div
              class="adaf-b-main-controls-right__download btn btn-light btn--small"
              :class="{ active: state.showDownloadOptions }"
              @click.stop="toggleOptions"
            >
              <span>{{ trans('organization.adaf.download') }}</span>
            </div>
            <div
              v-show="state.showDownloadOptions"
              ref="menu"
              class="adaf-b-main-controls-right__download-options"
            >
              <p @click="adafMixins.methods.downloadCurrentMonth">
                {{ trans('organization.adaf.download.cta.current_month') }}
              </p>
              <p @click="adafMixins.methods.downloadLast12Months">
                {{ trans('organization.adaf.download.cta.last_12_months') }}
              </p>
              <p @click="openCustomDatesModal">
                {{ trans('organization.adaf.download.cta.custom_dates') }}
              </p>
            </div>
          </div>
          <button class="btn btn--secondary btn--small adaf-b-main-controls__list-filters" @click="openFilters()">
            <span>{{ trans('filter.button.filters') }}</span>
            <span v-if="countActiveFilters > 0" class="pin">{{ countActiveFilters }}</span>
          </button>
          <AdafFiltersModal
            :is-opened="state.showFiltersModal"
            @onopencustomdatesmodal="openCustomDatesModal"
            @close="closeFilters"
            @onchange="onModalFilter"
          />
          <AdafDownloadCustomDatesModal
            :is-opened="state.showDownloadCustomDatesModal"
            @close="closeCustomDatesModal"
            @onselectdates="onSelectDates"
          />
        </div>
      </div>
    </div>
    <div class="adaf-b__main adaf-b-main top adaf-table">
      <AdafTable />
    </div>
  </div>
</template>
